import type { DatePickerProps } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import dayjs, { type Dayjs, type ManipulateType } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import UpdateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';

import {
  CHART_FREQUENCY,
  CHART_TERM_UNIT,
} from '@/entities/chart/model/constants';

import dayjsExtension from '../extensions/dayjs/extension';
import { RangeUnit } from './getDatePreset';
import { SHORT_EN_RELATIVE_TIME_LOCALE } from './relative-time-locale';

export const hourNum = 1000 * 60 * 60;
export const dayNum = hourNum * 24;
export const yearNum = dayNum * 365;

export const DATE_FORMAT = {
  base: 'YYYY-MM-DD',
  readable: 'MMM DD, YYYY',
  chat: 'YYYY-MM-DD  hh:mm a',
  short: 'MMM DD',
  readableHour: 'MMM DD, hh:mm A',
  timeOnly: 'hh:mm a',
  yearMonthOnly: 'MMM, YYYY',
};

export const MONTH_FORMAT_MAP: Record<string, string> = {
  Jan: '01',
  Feb: '02',
  Mar: '03',
  Apr: '04',
  May: '05',
  Jun: '06',
  Jul: '07',
  Aug: '08',
  Sep: '09',
  Oct: '10',
  Nov: '11',
  Dec: '12',
};

dayjs.extend(relativeTime);
dayjs.extend(UpdateLocale);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(dayjsExtension);

dayjs.updateLocale('en', {
  relativeTime: SHORT_EN_RELATIVE_TIME_LOCALE,
});

export const getTimezoneDayjs = (
  date?: string | number | Dayjs | null,
  timezone?: string,
) => {
  if (!date) return timezone ? dayjs().tz(timezone) : dayjs.tz();
  if (date instanceof dayjs) return timezone ? date.tz(timezone) : date;

  // If the input is just a date (YYYY-MM-DD format)
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return timezone
      ? dayjs.tz(`${date}T00:00:00`, timezone)
      : dayjs.tz(`${date}T00:00:00`);
  }

  // If the input is hourly format (YYYY-MM-DD HH) - 워크스페이스 타임존으로 해석
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}$/.test(date)) {
    return timezone
      ? dayjs.tz(`${date}:00:00`, timezone)
      : dayjs.tz(`${date}:00:00`);
  }

  const parsed = dayjs.utc(date);
  return timezone ? parsed.tz(timezone) : parsed.tz();
};

export const readableFormat = (dateString?: string) =>
  getTimezoneDayjs(dateString).format(DATE_FORMAT.readable);

export const getCurrentTimezone = () => {
  return dayjs.tz.guess();
};

export const parseWeeKFormat = (weekFormat: string): Dayjs => {
  const [year, week] = weekFormat.split(' W');
  return getTimezoneDayjs()
    .set('year', Number(year))
    .set('month', 0)
    .set('date', 1)
    .week(Number(week))
    .startOf('week');
};

export const parseDateFormatByRangeUnit = (
  label: string, // daily: YYYY-MM-DD, weekly: YYYY, [W]WW, monthly: MMM YYYY
  rangeUnit: RangeUnit,
) => {
  let start: Dayjs;
  let end: Dayjs;
  switch (rangeUnit) {
    case RangeUnit.Daily: {
      start = getTimezoneDayjs(label).startOf('day');
      end = start.addVal(1, 'day');
      break;
    }
    case RangeUnit.Weekly: {
      start = parseWeeKFormat(label);
      end = start.addVal(1, 'week');
      break;
    }
    case RangeUnit.Monthly: {
      const [monthStr, year] = label.split(' ');
      const numMonth = dayjs.utc(`${year}-${monthStr}-01`, 'YY-MMM-DD').month();
      start = getTimezoneDayjs()
        .set('year', Number(year) + 2000) // 2000년대로 한정
        .set('month', numMonth)
        .startOf('month');

      end = start.addVal(1, 'month');
      break;
    }

    default:
      throw new Error('Invalid RangeUnit');
  }

  return {
    start: start.toJSON(),
    end: end.toJSON(),
  };
};

export const getChartLabels = (
  dateRange: [Dayjs, Dayjs],
  formatCallback: (Dayjs: Dayjs) => string,
  unit: ManipulateType,
) => {
  const labels = [];
  let [startDate, endDate] = dateRange;
  startDate = startDate.startOf(unit);
  endDate = endDate.subtractVal(1, 'day');
  let condition = true;
  while (condition) {
    if (startDate.isAfter(endDate)) {
      condition = false;
    } else {
      labels.push(formatCallback(startDate));
      startDate = startDate.addVal(1, unit);
    }
  }
  return labels;
};

export const getFormatByRangeUnit = (rangeUnit: RangeUnit) => {
  switch (rangeUnit) {
    case RangeUnit.Hourly:
      return (dayjs: Dayjs) => dayjs.format('YYYY-MM-DD HH');
    case RangeUnit.Daily:
      return (dayjs: Dayjs) => dayjs.format(DATE_FORMAT.base);
    case RangeUnit.Weekly:
      return (dayjs: Dayjs) => `${dayjs.weekYear()} W${dayjs.week()}`;
    case RangeUnit.Monthly:
      return (dayjs: Dayjs) => dayjs.format('MMM YY');
    default:
      return (dayjs: Dayjs) => dayjs.format(DATE_FORMAT.base);
  }
};

export const getLabelsByRange = (props: {
  rangeUnit: RangeUnit;
  dateRange: [Dayjs, Dayjs] | null;
}) => {
  let labels: string[] = [];
  // 1: daily 2: weekly 3: monthly
  if (!props.dateRange) return labels;
  switch (props.rangeUnit) {
    case RangeUnit.Daily:
      labels = getChartLabels(
        props.dateRange,
        getFormatByRangeUnit(RangeUnit.Daily),
        'day',
      );
      break;
    case RangeUnit.Weekly:
      labels = getChartLabels(
        props.dateRange,
        getFormatByRangeUnit(RangeUnit.Weekly),
        'week',
      );
      break;
    case RangeUnit.Monthly:
      labels = getChartLabels(
        props.dateRange,
        getFormatByRangeUnit(RangeUnit.Monthly),
        'month',
      );
      break;
    default:
      break;
  }

  return labels;
};

export const getOpUnitType = (frequency: CHART_FREQUENCY) => {
  switch (frequency) {
    case CHART_FREQUENCY.HOUR:
      return 'hour';
    case CHART_FREQUENCY.DAY:
      return 'day';
    case CHART_FREQUENCY.WEEK:
      return 'week';
    case CHART_FREQUENCY.MONTH:
      return 'month';
  }
};

export const getPickerType = (
  frequency: CHART_FREQUENCY,
): DatePickerProps['picker'] => {
  switch (frequency) {
    case CHART_FREQUENCY.HOUR:
      return 'date';
    case CHART_FREQUENCY.DAY:
      return 'date';
    case CHART_FREQUENCY.WEEK:
      return 'week';
    case CHART_FREQUENCY.MONTH:
      return 'month';
  }
};

export const getOpUnitTypeByTermUnit = (unit: CHART_TERM_UNIT) => {
  switch (unit) {
    case CHART_TERM_UNIT.HOUR:
      return 'hour';
    case CHART_TERM_UNIT.DAY:
      return 'day';
    case CHART_TERM_UNIT.WEEK:
      return 'week';
    case CHART_TERM_UNIT.MONTH:
      return 'month';
    case CHART_TERM_UNIT.QUARTER:
      return 'month';
  }
};

export const getTermUnitLabel = (unit: CHART_TERM_UNIT) => {
  switch (unit) {
    case CHART_TERM_UNIT.HOUR:
      return 'hour';
    case CHART_TERM_UNIT.DAY:
      return 'day';
    case CHART_TERM_UNIT.WEEK:
      return 'week';
    case CHART_TERM_UNIT.MONTH:
      return 'month';
    case CHART_TERM_UNIT.QUARTER:
      return 'quarter';
  }
};

export const getRangeUnitByTermUnit = (unit: CHART_TERM_UNIT) => {
  switch (unit) {
    case CHART_TERM_UNIT.HOUR:
      return RangeUnit.Hourly;
    case CHART_TERM_UNIT.DAY:
      return RangeUnit.Daily;
    case CHART_TERM_UNIT.WEEK:
      return RangeUnit.Weekly;
    case CHART_TERM_UNIT.MONTH:
      return RangeUnit.Monthly;
    case CHART_TERM_UNIT.QUARTER:
      return RangeUnit.Monthly;
  }
};

export const getDateRangeWithEndDateOffset = (
  dates: [Dayjs, Dayjs] | null,
): [Dayjs, Dayjs] => {
  if (!dates) return [getTimezoneDayjs(), getTimezoneDayjs()];
  const [start, end] = dates;
  return [start, end.subtractVal(1, 'day')];
};

/** 주어진 날짜와 현재 시간의 차이를 시간/일 단위로 포맷 */
export const formatTimeDiff = (date: string): string => {
  const now = getTimezoneDayjs();
  const target = getTimezoneDayjs(date);
  const diffDays = now.diff(target, 'day');
  const diffHours = now.diff(target, 'hour');

  if (diffDays >= 1) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
  }
  if (diffHours >= 1) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
  }
  const diffMinutes = now.diff(target, 'minute');
  if (diffMinutes >= 1) {
    return `${diffMinutes} min${diffMinutes > 1 ? 's' : ''}`;
  }
  return 'just now';
};

export const getEndDateManipulatedDateRange = (
  dates: RangePickerProps['value'] | null,
  prevDates: [Dayjs, Dayjs] | null,
  opType: ManipulateType,
): [Dayjs, Dayjs] => {
  if (!dates) return prevDates || [getTimezoneDayjs(), getTimezoneDayjs()];
  const [start, end] = dates;
  const startDate = start || prevDates?.[0] || getTimezoneDayjs();
  const endDate = end || prevDates?.[1] || getTimezoneDayjs();
  return [
    startDate.startOf(opType || 'day').startOf('day'),
    endDate
      .endOf(opType || 'day')
      .addVal(1, 'day')
      .startOf('day'),
  ];
};
